import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { SchemaForm } from './common';
import Modal from '../shared/Modal';
import { whenOf } from '../nucleus/format';
import './VersionHistoryModal.css';

const FIELD_LABELS = {
  value_json: 'Content', 'Value Json': 'Content',
  title: 'Title', Title: 'Title',
  _tags_snapshot: 'Tags', 'Tags Snapshot': 'Tags', Tags: 'Tags',
  schema_id: 'Schema', 'Schema Id': 'Schema',
  'Content Timestamp': 'Date',
};

function fmtVal(val) {
  if (val == null) return null;
  if (typeof val === 'string') return val.replace(/\\n/g, ' ').slice(0, 150);
  if (Array.isArray(val)) return val.join(', ');
  if (typeof val === 'object') {
    if (val.content) return String(val.content).replace(/\\n/g, ' ').slice(0, 150);
    return Object.entries(val).filter(([, v]) => v != null && v !== '')
      .map(([k, v]) => `${k}: ${typeof v === 'string' ? v.slice(0, 80) : v}`).join(', ').slice(0, 150);
  }
  return String(val).slice(0, 150);
}

function ChangedFields({ fields }) {
  const list = Array.isArray(fields) ? fields
    : (fields ? Object.entries(fields).map(([field, diff]) => ({ field, old: diff?.old, new: diff?.new })) : []);
  if (!list.length) return null;
  return (
    <div className="vh-diff">
      {list.map((cf, i) => {
        const label = FIELD_LABELS[cf.field] || cf.field;
        if (label === 'Tags' && Array.isArray(cf.old) && Array.isArray(cf.new)) {
          const added = cf.new.filter(t => !cf.old.includes(t));
          const removed = cf.old.filter(t => !cf.new.includes(t));
          return (
            <div key={i} className="vh-diff__row">
              <span className="vh-diff__label">Tags:</span>{' '}
              {added.map((t, j) => <span key={'a' + j} className="vh-diff__add">+{t} </span>)}
              {removed.map((t, j) => <span key={'r' + j} className="vh-diff__del">-{t} </span>)}
            </div>
          );
        }
        const oldT = fmtVal(cf.old), newT = fmtVal(cf.new);
        return (
          <div key={i} className="vh-diff__row">
            <span className="vh-diff__label">{label}:</span>{' '}
            {oldT && newT ? (
              <><span className="vh-diff__old">{oldT}</span> <span className="vh-diff__arrow">&rarr;</span> <span className="vh-diff__new">{newT}</span></>
            ) : newT ? <span className="vh-diff__new">{newT}</span>
              : oldT ? <span className="vh-diff__old">{oldT}</span> : null}
          </div>
        );
      })}
    </div>
  );
}

/**
 * History modal — two complementary views:
 *  • Versions: restorable point-in-time states (full snapshot preview + restore)
 *  • Ledger: the chronological audit of every change (a revert shows up here as
 *    a new update), so both belong under one "history" surface.
 */
export default function VersionHistoryModal({ node, schema, encryptedFields = [], encKeys = null, onClose, onUpdateNode, initialView = 'versions' }) {
  const [view, setView] = useState(initialView); // 'versions' | 'ledger'
  const [versions, setVersions] = useState(null);
  const [ledger, setLedger] = useState(null);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [restoring, setRestoring] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.getNodeVersions(node.id).then(d => { if (!cancelled) setVersions(d); }).catch(() => { if (!cancelled) setVersions([]); });
    return () => { cancelled = true; };
  }, [node.id]);

  useEffect(() => {
    if (view !== 'ledger' || ledger) return;
    let cancelled = false;
    api.getNodeLedger(node.id).then(d => { if (!cancelled) setLedger(d); }).catch(() => { if (!cancelled) setLedger({ timeline: [], contributors: [] }); });
    return () => { cancelled = true; };
  }, [view, ledger, node.id]);

  const selected = versions?.[selectedIdx] || null;
  const snap = selected?.full_snapshot || {};
  const isCurrent = selectedIdx === 0;

  const handleRestore = async () => {
    if (!selected || isCurrent) return;
    setRestoring(true);
    try {
      await api.revertNodeToVersion(node.id, selected.version);
      if (onUpdateNode) {
        const fresh = await api.request(`/nodes/${node.id}?include_tags=true`);
        onUpdateNode({ ...fresh, _refreshOnly: true });
      }
      onClose();
    } catch (err) {
      alert(err.message || 'Failed to restore');
    } finally {
      setRestoring(false);
    }
  };

  const typeLabel = (t) => t === 'tag_added' ? 'tag +' : t === 'tag_removed' ? 'tag -' : t;
  const actorTag = (e) => e.contributor_type === 'agent' ? 'Agent' : e.contributor_type === 'third_party' ? 'App' : '';

  return (
    <Modal open onClose={onClose} className="vh-modal-wrap">
      <div className="vh-modal">
        {/* main area */}
        <div className="vh-main">
          {view === 'versions' ? (
            <>
              <h1 className="vh-title">{snap.title || node.title || 'Untitled'}</h1>
              {schema?.properties ? (
                <SchemaForm schema={schema} values={snap.value_json || {}} mode="preview" layout="properties" encryptedFields={encryptedFields} encKeys={encKeys} />
              ) : (
                <pre className="vh-json">{JSON.stringify(snap.value_json || {}, null, 2)}</pre>
              )}
            </>
          ) : (
            <div className="vh-ledger">
              <h2 className="vh-ledger__title">Ledger</h2>
              {!ledger ? <div className="vh-msg">Loading…</div>
                : !ledger.timeline?.length ? <div className="vh-msg">No history yet</div>
                : ledger.timeline.map((entry, idx) => (
                  <div key={idx} className={`vh-event vh-event--${entry.change_type}`}>
                    <span className="vh-event__dot" />
                    <div className="vh-event__body">
                      <div className="vh-event__head">
                        <span className="vh-event__who">
                          {entry.contributor_name}
                          {actorTag(entry) && <span className="vh-event__actor">{actorTag(entry)}</span>}
                        </span>
                        <span className="vh-event__when">{whenOf(entry.changed_at, { year: true })}</span>
                      </div>
                      <div className="vh-event__summary">
                        <span className={`vh-event__type vh-event__type--${entry.change_type}`}>{typeLabel(entry.change_type)}</span>
                        {entry.summary}
                      </div>
                      <ChangedFields fields={entry.changed_fields} />
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>

        {/* side — view toggle + list */}
        <aside className="vh-side">
          <div className="vh-side__header">
            <div className="vh-tabs">
              <button type="button" className={`btn vh-tab ${view === 'versions' ? 'vh-tab--active' : ''}`} onClick={() => setView('versions')}>Versions</button>
              <button type="button" className={`btn vh-tab ${view === 'ledger' ? 'vh-tab--active' : ''}`} onClick={() => setView('ledger')}>Ledger</button>
            </div>
            {/* Close button is provided by Modal */}
          </div>

          {view === 'versions' ? (
            <>
              <div className="vh-side__list">
                {!versions ? <div className="vh-msg">Loading…</div>
                  : versions.length === 0 ? <div className="vh-msg">No versions</div>
                  : versions.map((v, i) => (
                    <button key={v.version ?? i} className={`btn vh-version ${i === selectedIdx ? 'vh-version--active' : ''}`} onClick={() => setSelectedIdx(i)}>
                      <span className="vh-version__date">
                        {whenOf(v.changed_at, { year: true })}
                        {i === 0 && <span className="vh-version__current"> · Current</span>}
                      </span>
                      <span className="vh-version__meta">v{v.version} · {v.change_type}{v.changed_by_type === 'third_party' ? ' · App' : ''}</span>
                    </button>
                  ))}
              </div>
              <div className="vh-side__footer">
                <button className="btn vh-restore" onClick={handleRestore} disabled={restoring || isCurrent || !selected}>
                  {restoring ? 'Restoring…' : isCurrent ? 'Current version' : 'Restore this version'}
                </button>
              </div>
            </>
          ) : (
            <div className="vh-side__list">
              {ledger?.contributors?.length > 0 ? (
                <>
                  <div className="vh-side__label">Contributors</div>
                  {ledger.contributors.map((c, i) => (
                    <div key={i} className={`vh-contrib vh-contrib--${c.contributor_type}`}>
                      <span className="vh-contrib__name">{c.name}</span>
                      <span className="vh-contrib__count">{c.edit_count}</span>
                    </div>
                  ))}
                </>
              ) : (
                <div className="vh-msg">{ledger ? 'No contributors' : 'Loading…'}</div>
              )}
            </div>
          )}
        </aside>
      </div>
    </Modal>
  );
}
